/* ==========================================================================
   apiClient — the single place that talks to the backend.

   No service file below should call `fetch` directly. They should all call
   apiClient.request(...) so that: the base URL, auth header, JSON
   handling, and error shape are consistent everywhere, and so that turning
   off mock mode (config.useMockServices = false) is the only change needed
   to go live, service file by service file.

   NOTHING SECRET LIVES HERE. The Authorization header carries the user's
   own bearer token (issued by the backend at login) — never an API key,
   never a payment provider secret.
   ========================================================================== */

import { config } from "../config";
import { sessionStore } from "../state/session";
import type { ApiError } from "../types";

export class ApiClientError extends Error {
  code: string;
  status: number;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = "ApiClientError";
    this.code = error.code;
    this.status = status;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/** Thrown by request() if it is ever called while useMockServices is true.
 *  This is intentional: it should be impossible for a half-migrated
 *  service to silently fall through to a network call against an empty
 *  base URL. Each service decides per-function whether to use the mock
 *  implementation or apiClient — this guard just catches mistakes early. */
export class MockModeActiveError extends Error {
  constructor(path: string) {
    super(
      `apiClient.request("${path}") was called while config.useMockServices is true. ` +
        `This service function still needs its mock branch, or mock mode needs to be turned off.`
    );
    this.name = "MockModeActiveError";
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (config.useMockServices) {
    throw new MockModeActiveError(path);
  }

  const url = `${config.apiBaseUrl}${path}`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  const token = sessionStore.current?.token;
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    method: options.method ?? "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // Empty or non-JSON body — leave payload as null.
  }

  if (!res.ok) {
    const err = (payload as { error?: ApiError } | null)?.error ?? {
      code: "unknown_error",
      message: `Request to ${path} failed with status ${res.status}`,
    };
    throw new ApiClientError(res.status, err);
  }

  return payload as T;
}

export const apiClient = { request };
